'use client';

/**
 * Hero message arm (Wave H1) — "Message Tessa".
 *
 * The founder's reframe: the thread IS the product during onboarding. A
 * text is private, asynchronous, and survives an interruption; a cold call
 * demands availability right now. So the primary action opens a message
 * with the greeting (`Hi Tessa #REF`, or the page language's) already written — the ref is the attribution tag
 * that lets the first inbound stamp `onboarding_calls.campaign`.
 *
 * Two renders, chosen by the shell from the UA (never the viewport):
 *  - MOBILE: a red "Message Tessa" pill → platform-aware `sms:` deep link;
 *    an outline "WhatsApp" pill → `wa.me`; one 13px line. No "prefer a
 *    call?" — the call is asked for inside the thread (Camilo, 2026-08-21).
 *  - DESKTOP (`sms:` is dead): "Message Tessa" opens an in-place panel — a
 *    QR encoding the SAME `sms:` URI with the ref'd body (the ref survives
 *    the desktop→phone hop, so attribution does not), the number large with
 *    Copy/Copied, and "or message on WhatsApp" (WhatsApp Web). No call
 *    secondary. If the QR ever fails to render, the number + Copy stay.
 *
 * Dark closers re-tone `hsi-muted`/`hsi-strong` to white; the panel paints
 * its own `bg-background`, so nothing inside it uses those hooks.
 */

import { useEffect, useState, type Ref } from 'react';
import { Check, Copy, Loader2, MessageSquare } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { HeroCtaChannel, HeroCtaDevice } from '@/lib/onboarding/hero-cta';
import { HERO_CTA_COPY, type HeroCtaCopy } from '@/lib/onboarding/hero-cta-copy';
import { WhatsAppGlyph } from './WhatsAppGlyph';

export const MESSAGE_ARM_LINE = HERO_CTA_COPY.en.messageArmLine;
export const QR_PANEL_TITLE = HERO_CTA_COPY.en.qrPanelTitle;

const PRIMARY_PILL =
  'inline-flex h-12 items-center justify-center gap-2 rounded-full bg-[#EF4444] px-6 text-[15px] font-semibold text-white transition-all hover:bg-[#DC2626]';
const OUTLINE_PILL =
  'inline-flex h-12 items-center justify-center gap-2 rounded-full border border-border bg-background px-6 text-[15px] font-semibold text-foreground transition-colors hover:border-[#EF4444]';

export interface HeroMessageFirstProps {
  className?: string;
  /** The shell's IntersectionObserver target (hero_cta_view at ≥50%). */
  rootRef?: Ref<HTMLDivElement>;
  device: HeroCtaDevice;
  /** E.164 SMS number ('' when not configured). */
  smsNumber: string;
  /** E.164 WhatsApp number ('' when not configured). */
  waNumber: string;
  /** Platform-aware `sms:` link with the ref'd body ('' without SMS number). */
  smsHref: string;
  /** `wa.me` link with the ref'd body ('' without WhatsApp number). */
  waHref: string;
  /** The cross-platform `sms:` URI the desktop QR encodes. */
  qrUri: string;
  /** SMS number formatted for display (and for the QR's alt text). */
  smsDisplay: string;
  onAction: (channel: HeroCtaChannel) => void;
  copy: HeroCtaCopy;
}

export function HeroMessageFirst({
  className,
  rootRef,
  device,
  smsNumber,
  waNumber,
  smsHref,
  waHref,
  qrUri,
  smsDisplay,
  onAction,
  copy,
}: HeroMessageFirstProps) {
  const [panelOpen, setPanelOpen] = useState(false);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // Desktop panel mode needs an SMS number to encode; WhatsApp-only desktop
  // goes straight to WhatsApp Web (wa.me works where sms: does not).
  const panelMode = device === 'desktop' && !!smsNumber;
  const primaryHref = smsHref || waHref;
  const primaryChannel: HeroCtaChannel = smsHref ? 'sms' : 'whatsapp';
  // WhatsApp rides beside the primary on BOTH devices: wa.me opens WhatsApp
  // Web on desktop, so it is the one message path that works there without a
  // phone in hand. (It used to be mobile-only, which left desktop with a
  // single lonely pill and hid WhatsApp inside the QR panel.)
  const showWhatsAppPill = !!waHref && !!smsHref;

  // QR: generated client-side from the same sms: URI, loaded on demand so
  // the encoder never ships in the landing bundle for visitors who never
  // open the panel. Any failure leaves the number + Copy as the path.
  useEffect(() => {
    if (!panelOpen || !qrUri || qrDataUrl) return;
    let cancelled = false;
    import('qrcode')
      .then((mod) =>
        mod.toDataURL(qrUri, { width: 320, margin: 1, errorCorrectionLevel: 'M' }),
      )
      .then((url) => {
        if (!cancelled) setQrDataUrl(url);
      })
      .catch(() => {
        /* number + Copy remain — no QR-less fallback UI by design */
      });
    return () => {
      cancelled = true;
    };
  }, [panelOpen, qrUri, qrDataUrl]);

  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(t);
  }, [copied]);

  const openPanel = () => {
    onAction('qr_shown');
    setPanelOpen(true);
  };

  const copyNumber = async () => {
    onAction('copy_number');
    try {
      await navigator.clipboard.writeText(smsNumber);
      setCopied(true);
    } catch {
      // Clipboard blocked (insecure context / permissions) — the number is
      // visible and selectable right next to the button.
    }
  };

  return (
    <div
      ref={rootRef}
      className={cn(
        'mb-5 flex w-full max-w-[480px] flex-col gap-2',
        className,
        // The mount passes `text-left` — correct for the call form's labels
        // and error copy, wrong here: the pills would sit at the left edge of
        // a centered box and read as misaligned. From sm up, centre the
        // children; below sm the column keeps stretching, so the pills stay
        // full-width on a phone.
        'sm:items-center sm:text-center',
      )}
      data-hero-cta="message"
    >
      <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:justify-center">
        {panelMode ? (
          <button
            type="button"
            onClick={openPanel}
            aria-expanded={panelOpen}
            aria-controls="hero-message-panel"
            className={PRIMARY_PILL}
          >
            <MessageSquare className="h-4 w-4" />
            {copy.messageTessa}
          </button>
        ) : (
          <a
            href={primaryHref}
            onClick={() => onAction(primaryChannel)}
            {...(primaryChannel === 'whatsapp'
              ? { target: '_blank', rel: 'noopener noreferrer' }
              : {})}
            className={PRIMARY_PILL}
          >
            <MessageSquare className="h-4 w-4" />
            {copy.messageTessa}
          </a>
        )}
        {showWhatsAppPill && (
          <a
            href={waHref}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => onAction('whatsapp')}
            className={OUTLINE_PILL}
          >
            <WhatsAppGlyph className="inline-block h-4 w-4" />
            WhatsApp
          </a>
        )}
      </div>

      <p className="hsi-muted text-[13px] text-muted-foreground">{copy.messageArmLine}</p>

      {panelMode && panelOpen && (
        <div
          id="hero-message-panel"
          role="region"
          aria-label={copy.qrPanelLabel}
          className="mt-1 flex flex-col gap-3 rounded-2xl border border-border bg-background p-4 text-left sm:flex-row sm:items-center sm:gap-5"
        >
          <div className="flex h-[176px] w-[176px] shrink-0 items-center justify-center rounded-xl bg-white p-2">
            {qrDataUrl ? (
              // Data URL from the on-device encoder — next/image has nothing
              // to optimize here, and the sandbox must never fetch it remotely.
              // eslint-disable-next-line @next/next/no-img-element
              <img
                src={qrDataUrl}
                alt={copy.qrAlt(smsDisplay)}
                width={160}
                height={160}
                className="h-40 w-40"
              />
            ) : (
              <Loader2 className="h-5 w-5 animate-spin text-neutral-400" aria-hidden="true" />
            )}
          </div>
          <div className="flex min-w-0 flex-col gap-2">
            <p className="text-[15px] font-semibold leading-snug text-foreground">{copy.qrPanelTitle}</p>
            <div className="flex flex-wrap items-center gap-2">
              <span
                className="notranslate text-[22px] font-semibold tracking-tight text-foreground"
                translate="no"
              >
                {smsDisplay}
              </span>
              <button
                type="button"
                onClick={copyNumber}
                className="inline-flex h-8 items-center gap-1.5 rounded-full border border-border px-3 text-[13px] font-medium text-foreground transition-colors hover:border-[#EF4444]"
              >
                {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                {/* Wrapped: a conditional text node beside an element breaks
                    Google Translate's DOM surgery (react-google-translate). */}
                <span>{copied ? copy.copiedNumber : copy.copyNumber}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
